import { ThunkDispatch } from 'redux-thunk';
import { IAgent } from '@missionlabs/smartagent-lib-shared/build/agent';

import { getSingleUser } from 'services/api/api.agent';
import { getLiveContact } from 'services/api/api.contact';

import RootState from 'store/state';
import { AppFeatures, IFeature } from 'store/app/app.features';
import { WebSocketCompanionActions } from 'store/websocketCompanion/websocketCompanion.types';

import { constructTags, getIdFromARN } from '../../utils';
import ContactState, { IOriginalContactState } from '../contact/contact.state';

import CallTypes from './call.types';
import CallState, { CallConnection, Direction, MonitorStatus } from './call.state';
import { createConnection, updateConnection } from './call.utils';

export interface IncomingCallAction {
    id: string;
    number: string;
    contact: ContactState;
    direction: Direction;
}

export interface CallbackCallAction {
    id: string;
    number: string;
    contact: ContactState;
}

export interface OutboundCallAction {
    id: string;
    number: string;
    contact: ContactState;
}

export interface SetSensitiveAction {
    sensitive: boolean;
}

export interface MakeCallAction {
    id: string;
    number?: string;
    name?: string;
    endpoint?: {
        endpointARN: string;
        endpointId: string;
        type: connect.EndpointType;
        name: string;
        phoneNumber?: string;
        agentLogin?: string;
        queue?: string;
    };
}

export interface UpdateCallStatusAction {
    status: string;
    connectionId: string;
}

export type ConnectionId = string;

export interface SendDTMFAction {
    connectionId: ConnectionId;
    digit: string;
}

export interface InitiateLegacyMonitoringAction {
    contact: connect.Contact;
}

export interface InitiateEnhancedMonitoringAction {
    contact: connect.Contact;
    originalContact: IOriginalContactState;
    originalAgent: IAgent;
}

export interface SetConnectionsAction {
    connections: CallConnection[];
}

export interface UpdateMonitorStatusAction {
    status: MonitorStatus;
}

export interface SetMonitorStatusAction {
    status: MonitorStatus;
}

export type CallAction =
    | { type: typeof CallTypes.INCOMING_CALL; payload: IncomingCallAction }
    | { type: typeof CallTypes.CALLBACK_CALL; payload: CallbackCallAction }
    | { type: typeof CallTypes.OUTBOUND_CALL; payload: OutboundCallAction }
    | { type: typeof CallTypes.SET_SENSITIVE; payload: SetSensitiveAction }
    | { type: typeof CallTypes.END_CALL }
    | { type: typeof CallTypes.ACCEPT_CALL }
    | { type: typeof CallTypes.CALL_STARTED }
    | { type: typeof CallTypes.REJECT_CALL }
    | { type: typeof CallTypes.CALL_STARTED }
    | { type: typeof CallTypes.CALL_ENDED }
    | { type: typeof CallTypes.MAKE_CALL; payload: MakeCallAction }
    | { type: typeof CallTypes.UPDATE_CALL_STATUS; payload: UpdateCallStatusAction }
    | { type: typeof CallTypes.TRANSFER_CALL; payload: MakeCallAction }
    | { type: typeof CallTypes.HOLD; payload: ConnectionId }
    | { type: typeof CallTypes.SET_HOLD; payload: { connectionId: ConnectionId; hold: boolean } }
    | { type: typeof CallTypes.MUTE }
    | { type: typeof CallTypes.UNMUTE }
    | { type: typeof CallTypes.RESUME; payload: ConnectionId }
    | { type: typeof CallTypes.MISSED_CALL }
    | { type: typeof CallTypes.ADD_CONNECTION; payload: MakeCallAction }
    | { type: typeof CallTypes.LEAVE_CALL }
    | { type: typeof CallTypes.TOGGLE_CONNECTIONS }
    | { type: typeof CallTypes.SWAP_CONNECTIONS }
    | { type: typeof CallTypes.CONFERENCE_CONNECTIONS }
    | { type: typeof CallTypes.CONNECTION_CONNECTING; payload: ConnectionId }
    | { type: typeof CallTypes.CONNECTION_STARTED; payload: ConnectionId }
    | { type: typeof CallTypes.END_CONNECTION; payload: ConnectionId }
    | { type: typeof CallTypes.SEND_DTMF; payload: SendDTMFAction }
    | { type: typeof CallTypes.RECOVER_CALL; payload: CallState }
    | { type: typeof CallTypes.INITIATE_LEGACY_MONITORING; payload: InitiateLegacyMonitoringAction }
    | { type: typeof CallTypes.INITIATE_ENHANCED_MONITORING; payload: InitiateEnhancedMonitoringAction }
    | { type: typeof CallTypes.SET_SELECTED_CALL }
    | { type: typeof CallTypes.PARK_CALL_CONTACT_TRANSFER }
    | { type: typeof CallTypes.COMPLETE_CALL_ACW }
    | { type: typeof CallTypes.SET_CONNECTIONS; payload: SetConnectionsAction }
    | { type: typeof CallTypes.UPDATE_MONITOR_STATUS; payload: UpdateMonitorStatusAction }
    | { type: typeof CallTypes.SET_MONITOR_STATUS; payload: SetMonitorStatusAction };

export function startParkCall() {
    return async (dispatch: ThunkDispatch<RootState, undefined, CallAction>, getState: () => RootState) => {
        const state = getState();

        try {
            const { config } = (state.app.features.find(f => f.ID === AppFeatures.CALL_PARKING) ??
                {}) as IFeature<AppFeatures.CALL_PARKING>;

            if (!config) {
                throw new Error('Call parking config not found');
            }

            const connect = state.user.connects?.find(c => getIdFromARN(c.endpointARN) === config?.quickConnectID);

            if (!connect) {
                throw new Error('Call parking quick connect not found');
            }

            dispatch({
                type: CallTypes.PARK_CALL_CONTACT_TRANSFER,
            });
            dispatch(addConnection(connect.phoneNumber, connect.name, connect));
        } catch (err) {
            console.log(err.message);
        }
    };
}

export function updateConnections(connections: connect.VoiceConnection[]) {
    return async (dispatch: ThunkDispatch<RootState, undefined, CallAction>, getState: () => RootState) => {
        const { call } = getState();

        const existingConnections = call?.connections ?? [];

        try {
            const callConnections = connections.reduce<CallConnection[]>((currentCallConnections, connection) => {
                if (connection.getStatus().type === connect.ConnectionStateType.DISCONNECTED)
                    return currentCallConnections;

                const existingConnection = existingConnections.find(({ id }) => id === connection.connectionId);

                const newConnection = createConnection(connection);

                currentCallConnections.push(
                    existingConnection ? updateConnection(existingConnection, newConnection) : newConnection
                );

                return currentCallConnections;
            }, []);

            dispatch(setConnections(callConnections));
        } catch (error) {
            console.error('Error when attempting to update the connections: ', error);
        }
    };
}

export function setConnections(connections: CallConnection[]): CallAction {
    return {
        type: CallTypes.SET_CONNECTIONS,
        payload: { connections },
    };
}

export function incomingCall(connectionId: string, number: string, contact: ContactState) {
    return async (dispatch: ThunkDispatch<RootState, undefined, CallAction>, getState: () => RootState) => {
        const { websocketCompanion } = getState();
        const direction = Direction.INBOUND;

        websocketCompanion.client?.sendMessage(WebSocketCompanionActions.INCOMING_CALL, {
            name: contact.customer?.name ?? null,
            number,
            connectionId,
            direction,
            tags: constructTags(contact.attributes),
        });

        dispatch({
            type: CallTypes.INCOMING_CALL,
            payload: {
                id: connectionId,
                number,
                contact,
                direction,
            },
        });
    };
}

export function initiateLegacyMonitoring(contact: connect.Contact) {
    return {
        type: CallTypes.INITIATE_LEGACY_MONITORING,
        payload: { contact },
    };
}

export function initiateEnhancedMonitoring(contact: connect.Contact) {
    return async (dispatch: ThunkDispatch<RootState, undefined, CallAction>, getState: () => RootState) => {
        const { app, auth } = getState();

        const companyID = app.ID;
        const instanceID = app.instance?.ID;
        const token = auth.token;

        try {
            if (!instanceID) throw new Error('Instance ID not found');
            if (!token) throw new Error('Token not found');

            const originalContactID = contact.getOriginalContactId();

            const originalContact = await getLiveContact(originalContactID);
            const originalAgent = await getSingleUser(companyID, instanceID, token, originalContact.AgentInfo.Id);

            dispatch({
                type: CallTypes.INITIATE_ENHANCED_MONITORING,
                payload: { contact, originalContact, originalAgent },
            });
        } catch (error) {
            console.error('Error when attempting to initiate a monitoring session: ', error);
        }
    };
}

export function callbackCall(connectionId: string, number: string, contact: ContactState): CallAction {
    return {
        type: CallTypes.CALLBACK_CALL,
        payload: {
            id: connectionId,
            number,
            contact,
        },
    };
}

export function outboundCall(connectionId: string, number: string, contact: ContactState): CallAction {
    return {
        type: CallTypes.OUTBOUND_CALL,
        payload: {
            id: connectionId,
            number,
            contact,
        },
    };
}

export function updateMonitorStatus(status: MonitorStatus): CallAction {
    return {
        type: CallTypes.UPDATE_MONITOR_STATUS,
        payload: { status },
    };
}

export function setMonitorStatus(status: MonitorStatus): CallAction {
    return {
        type: CallTypes.SET_MONITOR_STATUS,
        payload: { status },
    };
}

export function setSelectedCall(): CallAction {
    return {
        type: CallTypes.SET_SELECTED_CALL,
    };
}
export function endCall(): CallAction {
    return {
        type: CallTypes.END_CALL,
    };
}

export function acceptCall(): CallAction {
    return {
        type: CallTypes.ACCEPT_CALL,
    };
}

export function callStarted(): CallAction {
    return {
        type: CallTypes.CALL_STARTED,
    };
}

export function rejectCall(): CallAction {
    return {
        type: CallTypes.REJECT_CALL,
    };
}

export function callEnded(): CallAction {
    return {
        type: CallTypes.CALL_ENDED,
    };
}

export interface IMakeCall {
    (number: string, name?: string, endpoint?: string): void;
}

export const makeCall = function (number: string, name?: string, endpoint?: any): CallAction {
    return {
        type: CallTypes.MAKE_CALL,
        payload: {
            id: String(Math.random()),
            number: number?.replace(/\s/g, '') ?? '',
            name,
            endpoint,
        },
    };
};

export function updateCallStatus(connectionId: string, status: string): CallAction {
    return {
        type: CallTypes.UPDATE_CALL_STATUS,
        payload: {
            status,
            connectionId,
        },
    };
}

export function transfer(number: string, name?: string, endpoint?: any): CallAction {
    return {
        type: CallTypes.TRANSFER_CALL,
        payload: {
            id: String(Math.random()),
            number,
            name,
            endpoint,
        },
    };
}

export function setHold(connectionId: ConnectionId, hold: boolean): CallAction {
    return {
        type: CallTypes.SET_HOLD,
        payload: { connectionId, hold },
    };
}

export function hold(connectionId: string): CallAction {
    return {
        type: CallTypes.HOLD,
        payload: connectionId,
    };
}

export function mute(): CallAction {
    return {
        type: CallTypes.MUTE,
    };
}

export function unmute(): CallAction {
    return {
        type: CallTypes.UNMUTE,
    };
}

export function resume(connectionId: string): CallAction {
    return {
        type: CallTypes.RESUME,
        payload: connectionId,
    };
}

export function missedCall() {
    return (dispatch: ThunkDispatch<RootState, undefined, CallAction>, getState: () => RootState) => {
        const { websocketCompanion } = getState();

        websocketCompanion.client?.sendMessage(WebSocketCompanionActions.MISSED_CALL);

        dispatch({
            type: CallTypes.MISSED_CALL,
        });
    };
}

export function addConnection(number: string, name?: string, endpoint?: any): CallAction {
    return {
        type: CallTypes.ADD_CONNECTION,
        payload: {
            id: String(Math.random()),
            number,
            name,
            endpoint,
        },
    };
}

export function leaveCall(): CallAction {
    return {
        type: CallTypes.LEAVE_CALL,
    };
}

export function toggleConnections(): CallAction {
    return {
        type: CallTypes.TOGGLE_CONNECTIONS,
    };
}

export function connectionConnecting(connectionId: string): CallAction {
    return {
        type: CallTypes.CONNECTION_CONNECTING,
        payload: connectionId,
    };
}

export function connectionStarted(connectionId: string): CallAction {
    return {
        type: CallTypes.CONNECTION_STARTED,
        payload: connectionId,
    };
}

export function endConnection(connectionId: string): CallAction {
    return {
        type: CallTypes.END_CONNECTION,
        payload: connectionId,
    };
}

export function conferenceConnections(): CallAction {
    return {
        type: CallTypes.CONFERENCE_CONNECTIONS,
    };
}

export function sendDTMF(connectionId: string, digit: string): CallAction {
    return {
        type: CallTypes.SEND_DTMF,
        payload: {
            connectionId,
            digit,
        },
    };
}

export function swapConnections(): CallAction {
    return {
        type: CallTypes.SWAP_CONNECTIONS,
    };
}

export function recoverCall(state: CallState): CallAction {
    return {
        type: CallTypes.RECOVER_CALL,
        payload: state,
    };
}

export function completeCallACW(): CallAction {
    return {
        type: CallTypes.COMPLETE_CALL_ACW,
    };
}
